import { FC } from 'react';

import { Box, Flex, SlideFade } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { LeaderboardPeriodResponse, LeaderboardStatPeriod, Period } from 'models';

import { ReactComponent as LeftArrowIcon } from 'icons/left-arr.svg';
import { ReactComponent as RightArrowIcon } from 'icons/right-arr.svg';

type Props = {
  stat?: LeaderboardStatPeriod;
  onDateChange(id: number | string): void;
  disabled: boolean;
  type?: Omit<Period, 'daily'>;
  isDateNavigation?: boolean;
  periodStat?: LeaderboardPeriodResponse;
};

const LeaderboardPeriodHeader: FC<Props> = ({
  disabled,
  stat,
  onDateChange,
  type,
  isDateNavigation = false,
  periodStat,
}) => {
  const isNextDateAvailable = (() => {
    if (!periodStat?.nextDate || !type) return false;

    const nextDate = dayjs(periodStat.nextDate);
    const currentDate = dayjs();

    if (type === 'weekly') {
      const currentWeekStart = currentDate.startOf('week');
      const currentWeekEnd = currentDate.endOf('week');
      return nextDate.isBefore(currentWeekEnd) || nextDate.isSame(currentWeekEnd, 'day');
    } else if (type === 'monthly') {
      const currentMonthStart = currentDate.startOf('month');
      const currentMonthEnd = currentDate.endOf('month');
      return nextDate.isBefore(currentMonthEnd) || nextDate.isSame(currentMonthEnd, 'day');
    }

    return false;
  })();

  return (
    <Flex
      pos="sticky"
      bottom="15px"
      justifyContent="space-between"
      align="center"
      fontSize="14px"
      lineHeight="16.8px"
      padding="11px 18px 11px"
      zIndex="10"
      w="100%"
      pointerEvents="none"
    >
      <SlideFade
        in={!!periodStat?.previousDate}
        onClick={() => {
          if (isDateNavigation && periodStat?.previousDate) {
            onDateChange(periodStat.previousDate);
          }
        }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'auto',
          cursor: 'pointer',
        }}
      >
        <Flex
          justify="center"
          align="center"
          gap="6px"
          fontFamily="Special Gothic Expanded One"
          fontSize="12px"
          lineHeight="1.2"
          textTransform="uppercase"
          fontWeight="400"
          border="1px solid rgba(255,255,255,0.1)"
          borderRadius="8px"
          bg="rgba(255,255,255,0.05)"
          backdropFilter="blur(30px)"
          w="80px"
          h="32px"
        >
          <Box w="12px" h="12px" as={LeftArrowIcon} />
          PREV
        </Flex>
      </SlideFade>
      <SlideFade
        in={!!periodStat?.nextDate && isNextDateAvailable}
        onClick={() => {
          if (isDateNavigation && periodStat?.nextDate && isNextDateAvailable) {
            onDateChange(periodStat.nextDate);
          }
        }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'auto',
          cursor: isNextDateAvailable ? 'pointer' : 'not-allowed',
          opacity: isNextDateAvailable ? 1 : 0.5,
        }}
      >
        <Flex
          justify="center"
          align="center"
          gap="6px"
          fontFamily="Special Gothic Expanded One"
          fontSize="12px"
          lineHeight="1.2"
          textTransform="uppercase"
          fontWeight="400"
          border="1px solid rgba(255,255,255,0.1)"
          borderRadius="8px"
          bg="rgba(255,255,255,0.05)"
          backdropFilter="blur(30px)"
          w="80px"
          h="32px"
        >
          NEXT
          <Box w="12px" h="12px" as={RightArrowIcon} />
        </Flex>
      </SlideFade>
    </Flex>
  );
};

export default LeaderboardPeriodHeader;
